import { BackTop, Layout } from 'antd'
import React, { useState, useEffect, lazy, Suspense } from 'react'
import LocationDashboard from '../Locations/Dashboard'
import { Route, Redirect, Switch, useLocation } from 'react-router-dom'
import { useSession, signOut, isAdmin, isManager } from '@slerp/accounts'
import { default as LocationSettings } from '../Locations/Settings'
import HelpCentre from '../Home/HelpCentre'
import NotFound from '../Misc/NotFound'
import PrintOrder from 'components/Orders/PrintOrder'
import Home from '../Home'
import Stream from './../Stream'
import StripeRedirectRoute from './StripeRedirectRoute'
import Loading from 'components/Utils/Loading'
import LayoutContent from './LayoutContent'
import Sidebar from './Sidebar'
import LayoutTitle from './LayoutTitle'
import { useHubspotChat } from 'hooks/useHubspotChat'
import { User } from 'packages/@slerp/client'
import styled from '@emotion/styled'
import { COLLAPSED_MARGIN_LEFT, EXPANDED_MARGIN_LEFT } from './constants'
import { useGlobalQuery } from 'hooks/useGlobalQuery'

const CategoryCutOffForm = lazy(
  () =>
    import('../Locations/AvailabilityRules/CategoryCutOffs/CategoryCutOffForm')
)
const SelectCreateRules = lazy(
  () =>
    import('../Locations/AvailabilityRules/CategoryCutOffs/SelectCreateRules')
)
const AvailabilityRules = lazy(() => import('./../Locations/AvailabilityRules'))
const SpecialAvailabilityForm = lazy(
  () =>
    import(
      '../Locations/AvailabilityRules/SpecialAvailability/SpecialAvailabilityForm'
    )
)

export interface PageHeaderType {
  title: string
  tooltipTitle?: React.ReactElement<any> | string
}

const StaffContainer = () => {
  const { user } = useSession()
  const location = useLocation()
  const canManage = isManager(user) || isAdmin(user)
  const [pathContext] = location?.pathname?.split('/').slice(1, 2)
  const isPrintOrder = pathContext === 'print-order'
  const [pageHeader, setPageHeader] = useState<PageHeaderType | null>(null)
  const hubspotJWT = useHubspotChat(user as User)

  const { collapsed } = useGlobalQuery()

  useEffect(() => {
    user?.store && setPageHeader({ title: user.store.name })
  }, [])

  useEffect(() => {
    if (isPrintOrder) return
    if (!hubspotJWT) return

    const identifyAndLoad = () => {
      // Must be set before (re)loading the widget

      ;(window as any).hsConversationsSettings = {
        loadImmediately: false,
        identificationEmail: user?.email,
        identificationToken: hubspotJWT
      }

      // If already loaded from a prior run, reset and remove before reloading

      if ((window as any).HubSpotConversations?.widget?.isLoaded?.()) {
        ;(window as any).HubSpotConversations.clear?.({ resetWidget: true })
        ;(window as any).HubSpotConversations.widget.remove?.()
      }

      ;(window as any).HubSpotConversations?.widget?.load?.()
    }

    if ((window as any).HubSpotConversations) {
      identifyAndLoad()
    } else {
      const t = setInterval(() => {
        if ((window as any).HubSpotConversations) {
          clearInterval(t)
          identifyAndLoad()
        }
      }, 200)
      return () => clearInterval(t)
    }
  }, [hubspotJWT, user?.email])

  useEffect(() => {
    switch (pathContext) {
      case '':
        setPageHeader({
          title: 'Insights',
          tooltipTitle: (
            <span>
              All your insights on this dashboard can be filtered by your
              store's locations and time period.
            </span>
          )
        })
        break
      case 'analytics':
        setPageHeader({
          title: 'Insights',
          tooltipTitle: (
            <span>
              All your insights on this dashboard can be filtered by your
              store's locations and time period.
            </span>
          )
        })
        break
      case 'heat-map':
        setPageHeader({
          title: 'Insights',
          tooltipTitle: (
            <span>
              All your insights on this dashboard can be filtered by your
              store's locations and time period.
            </span>
          )
        })
        break
      case 'location':
        setPageHeader({
          title: 'Locations',
          tooltipTitle: (
            <span>
              An{' '}
              <a
                href='https://support.slerp.com/en_gb/stores-and-locations-rkkCUtEpd'
                target='_blank'
                rel='noopener noreferrer'
              >
                overview
              </a>{' '}
              of your
              <br />
              stores' set up.
            </span>
          )
        })
        break
      case 'locations':
        setPageHeader({
          title: 'Locations',
          tooltipTitle: (
            <span>
              An{' '}
              <a
                href='https://support.slerp.com/en_gb/stores-and-locations-rkkCUtEpd'
                target='_blank'
                rel='noopener noreferrer'
              >
                overview
              </a>{' '}
              of your
              <br />
              stores' set up.
            </span>
          )
        })
        break
      case 'slerp-menu-manager':
        document.title = 'Slerp Controls - Menu Manager'
        setPageHeader({ title: 'Menu Manager' })
        break
      default:
        setPageHeader(null)
        break
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  if (pathContext === 'print-order')
    return <Route path='/print-order/:order_id' exact component={PrintOrder} />

  return (
    <StyledLayout hasSider collapsed={collapsed}>
      <Sidebar />
      <Layout className='site-layout'>
        <LayoutTitle pageHeader={pageHeader} />
        <LayoutContent>
          <Suspense fallback={<Loading />}>
            <Switch>
              <Route path='/' exact>
                <Redirect to='/analytics' />
              </Route>
              <Route path='/analytics' exact component={Home} />
              <Route path='/heat-map' exact component={Home} />
              <Route exact path='/locations'>
                <Redirect to={`/location/${user?.stores?.[0]?.store?.slug}`} />
              </Route>
              <Route
                path='/location/:slug'
                exact
                component={LocationDashboard}
              />
              <Route
                path='/location/:slug/orders'
                exact
                component={LocationDashboard}
              />
              <Route
                path='/location/:slug/inventory/same-day'
                exact
                component={LocationDashboard}
              />
              <Route
                path='/location/:slug/inventory/pre-orders'
                exact
                component={LocationDashboard}
              />
              <Route
                path='/location/:slug/settings'
                component={LocationSettings}
              />
              <Route
                path='/location/:slug/availability-rules'
                exact
                component={AvailabilityRules}
              />
              <Route
                path='/location/:slug/availability-rules/category-cut-off/rule/:rule_id'
                component={() => (
                  <CategoryCutOffForm pathname={location?.pathname} />
                )}
              />
              <Route
                path='/location/:slug/availability-rules/special-availability/rule/:rule_id'
                component={() => (
                  <SpecialAvailabilityForm pathname={location?.pathname} />
                )}
              />
              <StripeRedirectRoute path='/locations/stripe/connect' />
              {canManage && (
                <>
                  <Route
                    path='/location/:slug/inventory/same-day/edit/:tab'
                    exact
                    component={LocationDashboard}
                  />
                  <Route
                    path='/location/:slug/availability-rules/create'
                    exact
                    component={() => (
                      <SelectCreateRules pathname={location?.pathname} />
                    )}
                  />
                  <Route
                    path='/location/:slug/availability-rules/category-cut-off/create'
                    exact
                    component={() => (
                      <CategoryCutOffForm pathname={location?.pathname} />
                    )}
                  />
                  <Route
                    path='/location/:slug/availability-rules/special-availability/create'
                    exact
                    component={() => (
                      <SpecialAvailabilityForm pathname={location?.pathname} />
                    )}
                  />
                </>
              )}
              <Route path='/help-centre' component={HelpCentre} />
              <Route path='/slerp-menu-manager' exact component={Stream} />
              <Route component={NotFound} />
              <Route
                path='/sign-out'
                component={() => {
                  signOut()
                  return <></>
                }}
              />
            </Switch>
          </Suspense>
          <BackTop />
        </LayoutContent>
      </Layout>
    </StyledLayout>
  )
}

const StyledLayout = styled(Layout)<{ collapsed: boolean }>`
  .ant-btn:not(.ant-btn-icon-only),
  .ant-btn.ant-btn-default:not(.ant-btn-icon-only) {
    display: flex;
    align-items: center;
  }

  .site-layout {
    margin-left: ${({ collapsed }) =>
      collapsed ? `${COLLAPSED_MARGIN_LEFT}px` : `${EXPANDED_MARGIN_LEFT}px`};
    transition: margin-left 0.2s;
  }
`

export default StaffContainer
